import React from 'react';
import { Book } from 'lucide-react';
import { StorybookPage } from '../hooks/usePublishedStorybooks';
import { WordHighlighter } from './WordHighlighter';

interface StoryPageViewProps {
  page: StorybookPage;
  turnCodes: Record<string, string>;
  colorMappings: any[];
  phase: '1' | '2';
  pageNumber: number;
  totalPages: number;
  currentWordIndex?: number;
  isFirstPage?: boolean;
  storyTitle?: string;
  enabledColors?: Set<string>; // Color filtering - undefined = show all
}

export const StoryPageView: React.FC<StoryPageViewProps> = ({
  page,
  turnCodes,
  colorMappings,
  phase,
  pageNumber,
  totalPages,
  currentWordIndex = -1,
  isFirstPage = false,
  storyTitle,
  enabledColors
}) => {
  return (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 max-w-4xl mx-auto">
      {/* Image Section */}
      <div className="aspect-[16/10] bg-gradient-to-br from-blue-50 to-indigo-50 relative">
        {page.imageUrl ? (
          <img
            src={page.imageUrl}
            alt={page.sceneDescription || `Page ${pageNumber}`}
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Book className="w-24 h-24 text-blue-200" />
          </div>
        )}

        {/* Page Number Badge */}
        <div className="absolute top-4 right-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full shadow-md">
          <span className="text-sm font-medium text-gray-700">
            {pageNumber} / {totalPages}
          </span>
        </div>
      </div>

      {/* Text Section */}
      <div className="p-6 md:p-8 bg-gradient-to-b from-white to-gray-50">
        {/* Title on first page */}
        {isFirstPage && storyTitle && (
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
            {storyTitle}
          </h2>
        )}

        {/* Story Text with Turn Color and Word Highlighting */}
        <WordHighlighter
          text={page.text}
          turnCodes={turnCodes}
          colorMappings={colorMappings}
          phase={phase}
          currentWordIndex={currentWordIndex}
          fontSize={24}
          enabledColors={enabledColors}
        />
      </div>
    </div>
  );
};
